import type { ButtonProps } from '~/components/Button.vue'
import type { IconSize } from '~/utils/icon'
import { HeaderAuthButton } from '#components'
import {
  faArrowRightToBracket,
  faCircleArrowUpRight,
  faFolderOpen,
  faShoppingBasket,
  faUser,
} from '@fortawesome/pro-duotone-svg-icons'

export type Privacy = 'mask' | 'mask-user-input' | 'allow' | 'hidden'

export type BadgeValue = number | 'alert' | 'notification'

export interface NavItemProps extends ButtonProps {
  [key: `data-${string}`]: boolean | string | undefined
  open?: boolean
  label: string
  srOnly?: boolean
  hideExternalIcon?: boolean
  regularButton?: boolean
  privacy?: Privacy
  badge?: BadgeValue
  iconSize?: IconSize
  considerChildrenActive?: boolean
  external?: boolean
  onClick?: () => void
  childProps?: NavItemProps[]
  slots?: {
    post?: Component
  }
}

const overview = {
  label: 'Hjem',
  to: { name: 'oversikt-index' },
} satisfies NavItemProps

export const navItems = {
  home: {
    label: 'Hjem',
    to: { name: 'home' },
  },
  overview,
  overviewExternal: {
    ...overview,
    label: 'Bruk\xA0Juridika',
    external: true,
    hideExternalIcon: true,
    regularButton: true,
    variant: 'secondary',
    shape: 'square',
  },
  laws: {
    label: 'Lover og lovkommentarer',
    to: { name: 'laws-index' },
  },
  workspace: {
    label: 'Arbeidsområde',
    icon: faFolderOpen,
    considerChildrenActive: true,
    to: { name: 'workspace' },
  },
  profile: {
    label: 'Profil',
    icon: faUser,
    slots: {
      post: HeaderAuthButton,
    },
  },
  purchaseSubscription: {
    label: 'Kjøp\xA0abonnement',
    regularButton: true,
    variant: 'secondary',
    size: 'small',
    shape: 'rounded',
    to: { name: 'product-catalogue' },
  },
  login: {
    label: 'Logg\xA0inn',
    icon: faArrowRightToBracket,
    regularButton: true,
    variant: 'primary',
    size: 'small',
    shape: 'square',
    to: { name: 'login' },
  },
  cart: {
    label: 'Handlekurv',
    to: { name: 'cart' },
    icon: faShoppingBasket,
    iconSize: 'md',
  },
  account: {
    label: 'Din\xA0konto',
    to: { name: 'account-index' },
    icon: faUser,
  },
  tutorialVideos: {
    label: 'Hjelpevideoer',
    to: { name: 'tutorial-videos' },
    icon: faCircleArrowUpRight,
    hideExternalIcon: true,
  },
  customerService: {
    label: 'Kundeservice',
    to: {
      name: 'info-page',
      params: {
        slug: 'kundeservice',
      },
    },
  },
} satisfies Record<string, NavItemProps>

export const leftNavItems = {
  default: [
    navItems.laws,
    {
      label: 'Faglitteratur',
      to: { name: 'literature-index' },
    },
    {
      label: 'Tidsskrifter',
      to: { name: 'journals-index' },
    },
    {
      label: 'Forfattere',
      to: { name: 'authors-index' },
    },
    {
      label: 'Maler',
      to: { name: 'templates-index' },
    },
  ],
  marketing: [
    {
      label: 'Abonnement',
      to: { name: 'product-catalogue' },
    },
    {
      label: 'Artikler',
      to: { name: 'articles-index' },
    },
    {
      label: 'Podkast',
      // TODO: This should go to the podcasts overview page when/if it exists
      to: { name: 'podcast', params: { slug: 'takk-og-lov-med-anine-kierulf' } },
    },
    {
      label: 'Om\xA0Juridika',
      to: { name: 'about-index' },
    },
  ],
} satisfies Record<NavigationContext, NavItemProps[]>

export const profileDropdownNavItems = {
  marketing: [
    {
      ...navItems.account,
      external: true,
    },
    {
      ...navItems.workspace,
      external: true,
    },
    navItems.tutorialVideos,
    navItems.customerService,
  ],
  default: [
    navItems.account,
    {
      ...navItems.tutorialVideos,
      external: true,
    },
  ],
} satisfies Record<NavigationContext, NavItemProps[]>

export function getNavigationContext() {
  return inject(diKeys.navigationContext, 'default')
}

export function setNavigationContext(context: NavigationContext) {
  const isValid = NAVIGATION_CONTEXTS.includes(context)
  if (isValid) {
    provide(diKeys.navigationContext, context)
  }
}
